import { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@heroui/react';
import { requestPasswordReset } from '@/services/empresa/users.service';

interface ForgotPasswordModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const ForgotPasswordModal = ({ 
  isOpen = false, 
  onClose 
}: ForgotPasswordModalProps) => {
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const handleRequestPasswordReset = async () => {
    if (!email) {
      setToastMessage('Por favor ingresa tu correo electrónico.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    setLoading(true);
    try {
      await requestPasswordReset(email);
      setToastMessage('Instrucciones para restablecer tu contraseña han sido enviadas a tu correo electrónico.');
      setToastType('success');
      setShowToast(true);
      
      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        setShowToast(false);
        if (onClose) onClose();
      }, 3000);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setToastMessage('Error al enviar el correo de recuperación. Por favor verifica tu dirección de correo.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (onClose) onClose();
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={handleCloseModal}
        placement="center"
        backdrop="blur"
        size="md"
      >
        <ModalContent className="p-6 max-w-md w-full">
          <ModalHeader className="flex flex-col gap-1">
            <h2 className="text-xl font-bold text-center">¿Olvidaste tu contraseña?</h2>
          </ModalHeader>
          
          <ModalBody className="py-4">
            <p className="text-gray-600 text-center mb-4">
              Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
            </p>
            
            <Input
              label="Correo Electrónico"
              type="email"
              variant="bordered"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
              placeholder="tu@email.com"
            />
          </ModalBody>
          
          <ModalFooter className="flex flex-col">
            <Button 
              color="primary" 
              variant="solid" 
              className="w-full"
              onPress={handleRequestPasswordReset}
              isLoading={loading}
              isDisabled={!email || loading}
            >
              {loading ? 'Enviando...' : 'Enviar Instrucciones'}
            </Button>
            
            <Button 
              color="default" 
              variant="light" 
              className="w-full mt-2"
              onPress={handleCloseModal}
            >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
        
        {/* Toast Notification - positioned inside the modal to appear above backdrop */}
        {showToast && (
          <div 
            className={`absolute top-4 right-4 z-50 p-4 rounded-md shadow-lg transition-opacity duration-300 ${
              toastType === 'error' 
                ? 'bg-red-500 text-white' 
                : 'bg-green-500 text-white'
            }`}
          >
            <div className="flex items-center">
              <span>{toastMessage}</span>
              <button 
                onClick={() => setShowToast(false)}
                className="ml-4 text-white hover:text-gray-200 focus:outline-none"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ForgotPasswordModal;